import React, { useEffect, useRef } from 'react';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { history } from '../helpers';
import { clearStoredUserIfSessionInvalid, getValidToken, hasValidSession } from '../helpers/authUtils';
import { handleSessionExpired } from '../helpers/common';
import { isPageHidden, isPageVisible, getVisibilityChangeEvent } from '../helpers/pageVisibility';
import { alertActions, configActions, fontActions, gameActions, userActions } from '../actions';
import { selectUser } from '../selectors/auth';
import { PrivateRoute } from '../components';
import ErrorBoundary from '../components/ErrorBoundary';
import OfflineBanner from '../components/OfflineBanner';
import Toast from '../services/toast';
import { prefetchService, organizationService } from '../services';
import { getSocket } from '../services/socket';
import { HomePage } from '../components/Pages/HomePage';
import { LoginPage } from '../components/Pages/LoginPage';
import RegisterPage from '../components/Pages/RegisterPage/RegisterPage';
import FavoriteActivities from '../components/Pages/RegisterPage/FavoriteActivties';
import TermsandConditions from '../components/Pages/RegisterPage/TermsandConditions';
import MyProfile from '../components/Pages/MyProfile/MyProfile';
import EditProfile from '../components/Pages/MyProfile/EditProfile';
import EditFavoriteActivities from '../components/Pages/MyProfile/EditFavoriteActivities';
import GameQuestion from '../components/Pages/GameQuestion/GameQuestion';
import MyPoints from '../components/Pages/MyProfile/MyPoints';
import ScoreBoard from '../components/Pages/MyProfile/ScoreBoard';
import ChatPage from '../components/Pages/ChatPage/ChatPage';
import PurchaseAvatar from '../components/Pages/ShopPage/PurchaseAvatar';
import PurchaseGame from '../components/Pages/ShopPage/PurchaseGame';

/** Screen-level error boundary so game/chat failures do not take down the whole app. */
const GameQuestionWithBoundary = (props) => (
    <ErrorBoundary>
        <GameQuestion {...props} />
    </ErrorBoundary>
);

const ChatPageWithBoundary = (props) => (
    <ErrorBoundary>
        <ChatPage {...props} />
    </ErrorBoundary>
);

const RESUME_SESSION_CHECK_DEBOUNCE_MS = 500;

/** Idle timeout: log out after this many ms without user activity (only when tab is visible). */
const IDLE_TIMEOUT_MS = 120 * 60 * 1000; // 120 minutes (2 hours)

/** Throttle activity handling so we don't reset the idle timer on every mousemove. */
const IDLE_ACTIVITY_THROTTLE_MS = 1000;
const SYSTEM_FONT_STACK = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const DEFAULT_FONT_SIZES = {
    font14: 14,
    font16: 16,
    font18: 18,
    font20: 20,
    font22: 22,
    font25: 25,
    font30: 30,
    font36: 36,
    font40: 40,
};

const getSafeFontSize = (value, fallback) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

const getFontFormat = (fontUrl) => {
    const fontExtension = fontUrl.split('.').pop()?.split(/[?#]/)[0]?.toLowerCase();

    switch (fontExtension) {
        case 'otf':
            return 'opentype';
        case 'woff':
            return 'woff';
        case 'woff2':
            return 'woff2';
        case 'ttf':
        default:
            return 'truetype';
    }
};

/*
 * iPad / iOS Safari compatibility for session, socket and idle behaviour:
 * - Page Visibility: we use helpers (isPageHidden, isPageVisible, getVisibilityChangeEvent) which
 *   support both standard and webkit-prefixed API so visibility is detected when the user leaves
 *   Safari or switches app on iPad.
 * - Socket: disconnect on hidden stops polling; reconnect on visible. Idle: we use touchstart so
 *   activity is detected on touch devices; when the tab returns visible we re-check elapsed time
 *   because iOS may throttle setTimeout in background.
 */

const App = () => {
    const dispatch = useDispatch();
    const alert = useSelector((state) => state.alert);
    const fontDetails = useSelector((state) => state.fonts.fontdetails);
    const fontStyleRef = useRef(null);
    const user = useSelector(selectUser);
    const isLoggedIn = user?.data?.LoggedInStatus === 'Y';
    const studentProfileId = user?.data?.StudentProfileID;
    const classId = user?.data?.ClassID;
    const gameMenu = user?.data?.GameMenu;

    useEffect(() => {
        clearStoredUserIfSessionInvalid();
    }, []);

    useEffect(() => {
        const unlisten = history.listen(() => {
            dispatch(alertActions.clear());
        });

        return () => {
            unlisten();
        };
    }, [dispatch]);

    useEffect(() => {
        dispatch(fontActions.fetchFontDetails());
        dispatch(configActions.fetchAppConfigs());
        organizationService
            .fetchBranding()
            .then((branding) => {
                if (branding?.GAME_BROWSER_TITLE) {
                    document.title = branding.GAME_BROWSER_TITLE;
                }
            })
            .catch(() => {
                // Keep static fallback title if branding API is unavailable.
            });
    }, [dispatch]);

    useEffect(() => {
        if (!isLoggedIn || !studentProfileId || !hasValidSession()) return undefined;

        return prefetchService.warmStudentSession(user);
    }, [isLoggedIn, studentProfileId, classId, gameMenu, user]);

    useEffect(() => {
        if (!isLoggedIn || !hasValidSession()) return undefined;

        const socket = getSocket();
        if (!socket) return undefined;

        const handleSocketConnect = () => {
            dispatch(fontActions.fetchFontDetails(true));

            if (studentProfileId) {
                socket.emit('joinStudentRoom', {
                    StudentProfileID: studentProfileId,
                });
            }
        };

        const handleGameFontUpdated = (payload) => {
            if (!payload || typeof payload !== 'object') return;

            dispatch(fontActions.updateFontDetailsFromSocket(payload));
        };

        const handleStudentBlockStatusUpdated = (data) => {
            const incomingStudentProfileId = Number(
                data?.studentProfileId ?? data?.StudentProfileID
            );

            if (!Number.isFinite(incomingStudentProfileId)) return;
            if (incomingStudentProfileId !== Number(studentProfileId)) return;

            dispatch(gameActions.updateBlockedStatusFromSocket(data));
        };

        if (socket.connected) {
            handleSocketConnect();
        }

        socket.on('connect', handleSocketConnect);
        socket.on('gameFontUpdated', handleGameFontUpdated);
        socket.on('studentBlockStatusUpdated', handleStudentBlockStatusUpdated);

        return () => {
            socket.off('connect', handleSocketConnect);
            socket.off('gameFontUpdated', handleGameFontUpdated);
            socket.off('studentBlockStatusUpdated', handleStudentBlockStatusUpdated);
        };
    }, [dispatch, isLoggedIn, studentProfileId]);

    // Phase 5 – Background & resume: token check on visible + app-resumed signal for components.
    // When tab/app is hidden (e.g. user leaves Safari on iPad), disconnect socket to stop background polling.
    // Uses pageVisibility helper for iPad/Safari (standard + webkit-prefixed fallback).
    const visibilityEvent = getVisibilityChangeEvent();
    useEffect(() => {
        let debounceTimer = null;

        const handleVisibilityChange = () => {
            if (isPageHidden()) {
                if (debounceTimer) {
                    window.clearTimeout(debounceTimer);
                    debounceTimer = null;
                }
                // Socket stays connected even when hidden; socket.io handles backoff naturally
                // This prevents the "Reconnecting..." message and avoids reloading all chat messages
                return;
            }

            if (!isPageVisible()) return;

            debounceTimer = window.setTimeout(() => {
                debounceTimer = null;
                try {
                    if (localStorage.getItem('user') && getValidToken() === null) {
                        handleSessionExpired();
                        return;
                    }
                    document.dispatchEvent(new CustomEvent('appresumed'));
                    // Socket remains connected; no need to reconnect on tab visibility change
                } catch (e) {
                    document.dispatchEvent(new CustomEvent('appresumed'));
                }
            }, RESUME_SESSION_CHECK_DEBOUNCE_MS);
        };

        document.addEventListener(visibilityEvent, handleVisibilityChange);

        return () => {
            document.removeEventListener(visibilityEvent, handleVisibilityChange);
            if (debounceTimer) window.clearTimeout(debounceTimer);
        };
    }, [visibilityEvent]);

    // Idle timeout: log out after 5 minutes of inactivity. Timer runs even in background so we
    // logout as soon as 5 min pass when possible; when user returns we re-check in case the timer was throttled.
    useEffect(() => {
        if (!isLoggedIn) return;

        const timerRef = { current: null };
        let lastActivityAt = Date.now();

        const handleIdle = () => {
            dispatch(userActions.logout());
            history.push('/login', { idleTimeout: true });
        };

        const resetIdleTimer = () => {
            if (timerRef.current) window.clearTimeout(timerRef.current);
            timerRef.current = window.setTimeout(handleIdle, IDLE_TIMEOUT_MS);
        };

        const scheduleTimerFromNow = () => {
            if (timerRef.current) window.clearTimeout(timerRef.current);
            const elapsed = Date.now() - lastActivityAt;
            if (elapsed >= IDLE_TIMEOUT_MS) {
                handleIdle();
                return;
            }
            const remaining = IDLE_TIMEOUT_MS - elapsed;
            timerRef.current = window.setTimeout(handleIdle, remaining);
        };

        const onActivity = () => {
            const now = Date.now();
            if (now - lastActivityAt < IDLE_ACTIVITY_THROTTLE_MS) return;
            lastActivityAt = now;
            resetIdleTimer();
        };

        const onVisibilityChange = () => {
            if (isPageVisible()) {
                scheduleTimerFromNow();
            }
        };

        const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
        events.forEach((ev) => document.addEventListener(ev, onActivity));
        document.addEventListener(getVisibilityChangeEvent(), onVisibilityChange);

        resetIdleTimer();

        return () => {
            events.forEach((ev) => document.removeEventListener(ev, onActivity));
            document.removeEventListener(getVisibilityChangeEvent(), onVisibilityChange);
            if (timerRef.current) window.clearTimeout(timerRef.current);
        };
    }, [isLoggedIn, dispatch]);

    useEffect(() => {
        // Remove any previously injected font style (from prior run or fontDetails change)
        if (fontStyleRef.current && fontStyleRef.current.parentNode) {
            fontStyleRef.current.parentNode.removeChild(fontStyleRef.current);
            fontStyleRef.current = null;
        }

        const applyFont = async () => {
            try {
                if (
                    fontDetails &&
                    typeof fontDetails === 'object' &&
                    !Array.isArray(fontDetails) &&
                    String(fontDetails.EnabledFlag || '').toUpperCase() === 'N'
                ) {
                    const style = document.createElement('style');
                    style.innerHTML = `
                        :root {
                            --font: ${SYSTEM_FONT_STACK};
                        }
                    `;
                    document.head.appendChild(style);
                    fontStyleRef.current = style;
                    document.body.style.fontFamily = SYSTEM_FONT_STACK;
                    return;
                }

                if (
                    !fontDetails ||
                    !fontDetails.FontFilePath ||
                    typeof fontDetails.FontFilePath !== 'string' ||
                    fontDetails.FontFilePath.trim() === '' ||
                    !fontDetails.FontName ||
                    typeof fontDetails.FontName !== 'string' ||
                    fontDetails.FontName.trim() === ''
                ) {
                    return;
                }

                const fontUrl = fontDetails.FontFilePath.trim();
                const fontFamily = fontDetails.FontName.trim();

                // Allowlist: prevent XSS via font config (no quotes, angle brackets, or script protocols)
                const safeFontName = /^[\w\s\-'.]+$/;
                const dangerousInUrl = /["'<>`\\;]|javascript:|data:|vbscript:/i;
                if (!safeFontName.test(fontFamily) || fontFamily.length > 200) return;
                if (dangerousInUrl.test(fontUrl)) return;
                const font14 = getSafeFontSize(fontDetails.font14, DEFAULT_FONT_SIZES.font14);
                const font16 = getSafeFontSize(fontDetails.font16, DEFAULT_FONT_SIZES.font16);
                const font18 = getSafeFontSize(fontDetails.font18, DEFAULT_FONT_SIZES.font18);
                const font20 = getSafeFontSize(fontDetails.font20, DEFAULT_FONT_SIZES.font20);
                const font22 = getSafeFontSize(fontDetails.font22, DEFAULT_FONT_SIZES.font22);
                const font25 = getSafeFontSize(fontDetails.font25, DEFAULT_FONT_SIZES.font25);
                const font30 = getSafeFontSize(fontDetails.font30, DEFAULT_FONT_SIZES.font30);
                const font36 = getSafeFontSize(fontDetails.font36, DEFAULT_FONT_SIZES.font36);
                const font40 = getSafeFontSize(fontDetails.font40, DEFAULT_FONT_SIZES.font40);

                const fontFormat = getFontFormat(fontUrl);

                // Dynamically add the @font-face style
                const style = document.createElement('style');
                style.innerHTML = `
                    @font-face {
                        font-family: "${fontFamily}";
                        src: url("${fontUrl}") format("${fontFormat}");
                        font-display: swap;
                    }
                    :root {
                        --font: "${fontFamily}";
                        --f14: ${font14}px;
                        --f16: ${font16}px;
                        --f18: ${font18}px;
                        --f20: ${font20}px;
                        --f22: ${font22}px;
                        --f25: ${font25}px;
                        --f30: ${font30}px;
                        --f36: ${font36}px;
                        --f40: ${font40}px;
                    }
                `;
                document.head.appendChild(style);
                fontStyleRef.current = style;

                document.body.style.fontFamily = fontFamily;
            } catch (error) {
                // ignore
            }
        };

        applyFont();

        return () => {
            if (fontStyleRef.current && fontStyleRef.current.parentNode) {
                fontStyleRef.current.parentNode.removeChild(fontStyleRef.current);
                fontStyleRef.current = null;
            }
            document.body.style.fontFamily = '';
        };
    }, [fontDetails]);


    return (
        <>
            <OfflineBanner />
            {alert.message && (
                <div className={`alert ${alert.type}`}>{alert.message}</div>
            )}
            <Toast />
            <Router history={history}>
                <Switch>
                    {/* Pages that require LoggedInStatus === "Y" */}
                    <PrivateRoute exact path="/" component={HomePage} restrictedToLoggedInStatus />
                    <PrivateRoute path="/myprofile" component={MyProfile} restrictedToLoggedInStatus />
                    <PrivateRoute path="/editprofile" component={EditProfile} restrictedToLoggedInStatus />
                    <PrivateRoute path="/editfavoriteactivities" component={EditFavoriteActivities} restrictedToLoggedInStatus />
                    <PrivateRoute path="/gamequestion" component={GameQuestionWithBoundary} restrictedToLoggedInStatus />
                    <PrivateRoute path="/mypoints" component={MyPoints} restrictedToLoggedInStatus />
                    <PrivateRoute path="/scoreboard" component={ScoreBoard} restrictedToLoggedInStatus />
                    <PrivateRoute path="/chat" component={ChatPageWithBoundary} restrictedToLoggedInStatus />
                    <PrivateRoute path="/purchaseavatar" component={PurchaseAvatar} restrictedToLoggedInStatus />
                    <PrivateRoute path="/purchasegame" component={PurchaseGame} restrictedToLoggedInStatus />

                    {/* Pages that only require localStorage.getItem('user') */}
                    <PrivateRoute path="/register" component={RegisterPage} />
                    <PrivateRoute path="/favouriteactivities" component={FavoriteActivities} />
                    <PrivateRoute path="/termsandconditions" component={TermsandConditions} />

                    <Route path="/login" component={LoginPage} />
                    <Redirect from="*" to="/" />
                </Switch>
            </Router>
        </>
    );
};

export { App };
